import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo} from 'react';
import {ApiResponse} from '@legit-health/backend-client';
import {useBodySites, useScoringSystems} from '@legit-health/backend-client-elements';
import {
  BodySite,
  BodySiteCode,
  BodySiteId,
  indexArrayBy,
  ScoringSystem,
  ScoringSystemId,
  Pathology,
  PathologyId,
  CompanyId,
  PathologyCode
} from '@legit-health/ui';
import {useQueryClient} from '@tanstack/react-query';
import useLoggedUserContext, {useCompany} from '@/shared/contexts/UserContext/useUserContext';
import useAppInfo from './useAppInfo';
import usePathologies, {getQueryKey as getPathologiesQueryKey} from './usePathologies';
import cachedDataState from './utils/cachedDataState';

export type SharedDataContextValue = {
  customPathologies: Pathology[];
  pathologies: Pathology[];
  pathologiesById: Record<PathologyId, Pathology>;
  pathologiesByCode: Record<PathologyCode, Pathology>;
  bodySites: BodySite[];
  bodySitesById: Record<BodySiteId, BodySite>;
  bodySitesByCode: Record<BodySiteCode, BodySite>;
  scoringSystems: ScoringSystem[];
  scoringSystemsById: Record<ScoringSystemId, ScoringSystem>;
  clear: () => void;
  addPathology: (pathology: Pathology, companyId: CompanyId) => void;
  invalidatePathologies: () => void;
  getQuestionnairesForPathology: (pathology: Pathology) => ScoringSystem[];
};

const SharedDataContext = createContext<SharedDataContextValue>({
  customPathologies: [],
  pathologies: [],
  pathologiesById: {},
  pathologiesByCode: {},
  bodySites: [],
  bodySitesById: {},
  bodySitesByCode: {},
  scoringSystems: [],
  scoringSystemsById: {},
  clear: () => {},
  addPathology: () => {},
  invalidatePathologies: () => {},
  getQuestionnairesForPathology: () => []
});

export function useSharedDataContext() {
  return useContext(SharedDataContext);
}

export function SharedDataContextProvider({children}: {children: ReactNode}) {
  const {user} = useLoggedUserContext();
  const {
    dataImportedAt: cachedDataImportedAt,
    locale: cachedLocale,
    bodySites: cachedBodySites,
    scoringSystems: cachedScoringSystems
  } = cachedDataState.get();
  const {
    data: {data: appInfoData}
  } = useAppInfo();
  const dataImportedAt = appInfoData?.dataImportedAt;
  const isCacheFresh = cachedDataImportedAt === dataImportedAt && user.locale === cachedLocale;
  const queryClient = useQueryClient();
  const company = useCompany();
  const {
    data: {data: bodySites}
  } = useBodySites({initialData: isCacheFresh && cachedBodySites ? cachedBodySites : undefined});
  const {
    data: {data: pathologies}
  } = usePathologies({isCacheFresh});
  const {
    data: {data: scoringSystems}
  } = useScoringSystems({
    initialData: isCacheFresh && cachedScoringSystems ? cachedScoringSystems : undefined
  });

  useEffect(
    function () {
      cachedDataState.set((c) => ({
        ...c,
        scoringSystems
      }));
    },
    [scoringSystems]
  );

  useEffect(
    function () {
      cachedDataState.set((c) => ({
        ...c,
        bodySites
      }));
    },
    [bodySites]
  );

  useEffect(
    function () {
      cachedDataState.set((c) => ({
        ...c,
        pathologies
      }));
    },
    [pathologies]
  );
  const customPathologies = pathologies.filter((p) => p.isCustom);

  const addPathology = useCallback(
    (pathology: Pathology, companyId: CompanyId) =>
      queryClient.setQueryData<ApiResponse<Pathology[]> | null>(
        getPathologiesQueryKey(companyId),
        (value) => ({
          isSuccessfull: true,
          status: 200,
          data: value?.data ? [...value.data, pathology] : [pathology]
        })
      ),
    [queryClient]
  );

  const invalidatePathologies = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getPathologiesQueryKey(company.id)
      }),
    [company.id, queryClient]
  );

  const scoringSystemsById = useMemo(() => indexArrayBy(scoringSystems, 'id'), [scoringSystems]);
  const getQuestionnairesForPathology = useCallback(
    (pathology: Pathology) =>
      pathology.scoringSystemsIds
        .map((ssId) => scoringSystemsById[ssId])
        .filter((ss) => ss && ss.requiresQuestionnaire) as ScoringSystem[],
    [scoringSystemsById]
  );

  const value = useMemo(
    () => ({
      bodySites,
      bodySitesById: indexArrayBy(bodySites, 'id'),
      bodySitesByCode: indexArrayBy(bodySites, 'code'),
      customPathologies,
      pathologies,
      pathologiesById: indexArrayBy(pathologies, 'id'),
      pathologiesByCode: indexArrayBy(pathologies, 'code'),
      scoringSystems,
      scoringSystemsById,
      clear: () => cachedDataState.delete(),
      addPathology,
      invalidatePathologies,
      getQuestionnairesForPathology
    }),
    [
      bodySites,
      pathologies,
      scoringSystems,
      customPathologies,
      addPathology,
      invalidatePathologies,
      scoringSystemsById,
      getQuestionnairesForPathology
    ]
  );

  return <SharedDataContext.Provider value={value}>{children}</SharedDataContext.Provider>;
}
